import * as React from 'react';
import * as PropTypes from 'prop-types';
import moment from 'moment/moment';
import { DateFormat } from '../../../store/pages/venues/types';
import { clickHandler, formatMoment, isNullOrEmpty } from '../../../utils/util';
import { ProductStockLevel, ProductStockStatus } from '../../../store/pages/productStock/types';
import Loading from '../../global/loading';
import { bindActionCreators, Dispatch } from 'redux';
import * as LoginActions from '../../../store/pages/login/actions';
import { connect } from 'react-redux';
import ClickableHeader from '../../global/clickableHeader';
import { stringComparer } from '../../../utils/comparers';
import { ApplicationState } from '../../../store';
import * as ProductStockActions from '../../../store/pages/productStock/actions'

enum SortColumn {
    ProductName,
    ProductVariantName,
    Date0CurrentStock,
    Date0Available,
    Date0AllocatedPaid,
    Date0AllocatedUnpaid,
    Date1CurrentStock,
    Date1Available,
    Date1AllocatedPaid,
    Date1AllocatedUnpaid,
    Date2CurrentStock,
    Date2Available,
    Date2AllocatedPaid,
    Date2AllocatedUnpaid,
}

interface Sort {
    column: SortColumn;
    direction: 1 | -1;
}

interface MappedReduxState {
    stockDate: Date;
    stockLevels: ProductStockLevel[];
    isLoadingProductStock: boolean;
}

interface ReduxActions {
    loadVenueStockOverview: (stockDate: Date) => void;
    logout: () => void;
}

interface LocalProps {
    venueId: string;
    dateFormat: DateFormat;
    filterSearchTerm: string;
    selectedDate: moment.Moment;
    onSelectedProductChanged: (productId: string, productVariantId: string | null) => void;
}

type StockLevelOverviewProps = MappedReduxState & ReduxActions & LocalProps;

interface StockLevelOverviewState {
    sort: Sort;
}

class StockLevelOverview extends React.Component<StockLevelOverviewProps, StockLevelOverviewState> {
    constructor(props: StockLevelOverviewProps) {
        super(props);
        this.state = { sort: { column: SortColumn.ProductName, direction: 1 } };
    }

    static contextTypes = {
        t: PropTypes.func
    }

    componentDidMount() {
        const { selectedDate, venueId, loadVenueStockOverview } = this.props;
        if (moment.isMoment(selectedDate) && !isNullOrEmpty(venueId)) {
            loadVenueStockOverview(selectedDate.toDate());
        }
    }

    componentDidUpdate(prevProps: StockLevelOverviewProps) {
        const { selectedDate, venueId, loadVenueStockOverview } = this.props;
        const selectedDateChanged = moment.isMoment(selectedDate) && !prevProps.selectedDate.isSame(selectedDate);
        const venueIdChanged = venueId !== prevProps.venueId;
        if (!isNullOrEmpty(venueId)  && (selectedDateChanged || venueIdChanged)) {
            loadVenueStockOverview(selectedDate.toDate());
        }
    }

    selectedItemChanged = (productStockLevel: ProductStockLevel) => {
        const { onSelectedProductChanged } = this.props;
        onSelectedProductChanged(productStockLevel.productId, productStockLevel.productVariantId);
    }

    updateSort = (col: SortColumn) => {
        this.setState(prevState => {
            const { sort } = prevState;
            const newDirection = sort.column === col && sort.direction === -1 ? 1 : -1;
            return {
                sort: {
                    column: col,
                    direction: newDirection
                }
            };
        });
    }
    
    applyProductFilter = (stockLevel: ProductStockLevel) => {
        const { filterSearchTerm } = this.props;
        const lowerFilterSearchTerm = filterSearchTerm.toLowerCase();
        return (isNullOrEmpty(lowerFilterSearchTerm) || stockLevel.productName.toLowerCase().includes(lowerFilterSearchTerm))
            ||
            (isNullOrEmpty(lowerFilterSearchTerm) || stockLevel.productVariantName?.toLowerCase()?.includes(lowerFilterSearchTerm));
    }

    render() {
        const { t } = this.context;
        const { dateFormat, isLoadingProductStock, stockLevels } = this.props;
        const { sort } = this.state;

        if (isLoadingProductStock) return <Loading />;
        if (stockLevels.length < 1) return null;
        //if (loadingError) return <ApiError error={loadingError} />;

        const sortColumnMap = (stockLevels.length > 0)
            ? stockLevels[0].quantities.map((_, i) => ({
                currentStock: SortColumn[`Date${i}CurrentStock` as keyof typeof SortColumn],
                availableStock: SortColumn[`Date${i}Available` as keyof typeof SortColumn],
                paidAlloctedStock: SortColumn[`Date${i}AllocatedPaid` as keyof typeof SortColumn],
                unpaidAlloctedStock: SortColumn[`Date${i}AllocatedUnpaid` as keyof typeof SortColumn],
            }))
            : [];

        return <div>
            {(stockLevels.length === 0) && (
                <div className='text-center alert alert-info'>{t('StockLevelOverview:noResults')}</div>
            )}

            {(stockLevels.length > 0) && (
                <table className='table table-striped table-sm table-condensed stock-overview-data-table'>
                    <thead>
                        <tr key='main-header'>
                            <th></th>
                            <th></th>
                            <>{stockLevels[0].quantities.map((q, i) => <th key={i} colSpan={4} className='text-center bordered'>{formatDateHeader(q.dateInLocalTime, dateFormat, t)}</th>)}</>
                        </tr>
                        <tr key='sub-header'>
                            <ClickableHeader tableHeaderClassName='bordered' select={() => this.updateSort(SortColumn.ProductName)} selected={sort.column === SortColumn.ProductName} direction={sort.direction} headerKey='StockLevelOverview:tableHeaderProduct' />
                            <ClickableHeader tableHeaderClassName='bordered' select={() => this.updateSort(SortColumn.ProductVariantName)} selected={sort.column === SortColumn.ProductVariantName} direction={sort.direction} headerKey='StockLevelOverview:tableHeaderProductVariant' />
                            {stockLevels[0].quantities.map((_, i) => <React.Fragment key={i}>
                                <ClickableHeader tableHeaderClassName='bordered' select={() => this.updateSort(sortColumnMap[i].currentStock)} selected={sort.column === sortColumnMap[i].currentStock} direction={sort.direction} headerKey='StockLevelOverview:tableHeaderCurrentStock' rightAlignHeaderText={true} />
                                <ClickableHeader tableHeaderClassName='bordered' select={() => this.updateSort(sortColumnMap[i].paidAlloctedStock)} selected={sort.column === sortColumnMap[i].paidAlloctedStock} direction={sort.direction} headerKey='StockLevelOverview:tableHeaderPaidAllocatedStock' rightAlignHeaderText={true} />
                                <ClickableHeader tableHeaderClassName='bordered' select={() => this.updateSort(sortColumnMap[i].unpaidAlloctedStock)} selected={sort.column === sortColumnMap[i].unpaidAlloctedStock} direction={sort.direction} headerKey='StockLevelOverview:tableHeaderUnpaidAllocatedStock' rightAlignHeaderText={true} />
                                <ClickableHeader tableHeaderClassName='bordered' select={() => this.updateSort(sortColumnMap[i].availableStock)} selected={sort.column === sortColumnMap[i].availableStock} direction={sort.direction} headerKey='StockLevelOverview:tableHeaderAvailableStock' rightAlignHeaderText={true} />
                            </React.Fragment>)}
                        </tr>
                    </thead>
                    <tbody>
                        {stockLevels
                            .filter(this.applyProductFilter)
                            .sort((sl1, sl2) => sortProducts(sl1, sl2, sort))
                            .map((productStockLevel, i) => (
                                <tr key={i}>
                                    <td>
                                        <button className='btn btn-link btn-no-padding' onClick={e => clickHandler(e, () => this.selectedItemChanged(productStockLevel))}>
                                            {productStockLevel.productName}
                                        </button>
                                    </td>
                                    <td>{productStockLevel.productVariantName ?? ''}</td>
                                    {productStockLevel.quantities.map((q, i) => {
                                        return <React.Fragment key={i}>
                                            <td className='text-right bordered'>{q.currentStock}</td>
                                            <td className='text-right'>{q.paidAllocatedStock}</td>
                                            <td className='text-right'>{q.unpaidAllocatedStock}</td>
                                            <td className={`text-right ${getItemClass(q.stockState)}`}>{q.availableStock}</td>
                                        </React.Fragment>
                                    })}
                                </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    }
}

function formatDateHeader(date: Date, dateFormat: DateFormat, t: (key: string) => string) {
    if (date.isToday()) return t('Global:today');
    if (date.isTomorrow()) return t('Global:tomorrow');
    return formatMoment(moment(date), dateFormat);
}

function sortProducts(sl1: ProductStockLevel, sl2: ProductStockLevel, sort: Sort)
{
    let val = 0;
    switch (sort.column) {
        case SortColumn.ProductName:
            val = stringComparer(sl1.productName.toLocaleLowerCase(), sl2.productName.toLocaleLowerCase());
            if (val === 0 && sl1.productVariantName && sl2.productVariantName) {
                val += stringComparer(sl1.productVariantName.toLocaleLowerCase(), sl2.productVariantName.toLocaleLowerCase());
            }
            break;
        case SortColumn.ProductVariantName:
            val = stringComparer((sl1.productVariantName ?? '').toLocaleLowerCase(), (sl2.productVariantName ?? '').toLocaleLowerCase());
            break;
        case SortColumn.Date0CurrentStock:
            if (sl1.quantities.length === 0) break;
            val = (sl1.quantities[0].currentStock ?? 0) - (sl2.quantities[0].currentStock ?? 0);
            break;
        case SortColumn.Date0Available:
            if (sl1.quantities.length === 0) break;
            val = (sl1.quantities[0].availableStock ?? 0) - (sl2.quantities[0].availableStock ?? 0);
            break;
        case SortColumn.Date0AllocatedPaid:
            if (sl1.quantities.length === 0) break;
            val = (sl1.quantities[0].paidAllocatedStock ?? 0) - (sl2.quantities[0].paidAllocatedStock ?? 0);
            break;
        case SortColumn.Date0AllocatedUnpaid:
            if (sl1.quantities.length === 0) break;
            val = (sl1.quantities[0].unpaidAllocatedStock ?? 0) - (sl2.quantities[0].unpaidAllocatedStock ?? 0);
            break;
        case SortColumn.Date1CurrentStock:
            if (sl1.quantities.length < 2) break;
            val = (sl1.quantities[1].currentStock ?? 0) - (sl2.quantities[1].currentStock ?? 0);
            break;
        case SortColumn.Date1Available:
            if (sl1.quantities.length < 2) break;
            val = (sl1.quantities[1].availableStock ?? 0) - (sl2.quantities[1].availableStock ?? 0);
            break;
        case SortColumn.Date1AllocatedPaid:
            if (sl1.quantities.length < 2) break;
            val = (sl1.quantities[1].paidAllocatedStock ?? 0) - (sl2.quantities[1].paidAllocatedStock ?? 0);
            break;
        case SortColumn.Date1AllocatedUnpaid:
            if (sl1.quantities.length < 2) break;
            val = (sl1.quantities[1].unpaidAllocatedStock ?? 0) - (sl2.quantities[1].unpaidAllocatedStock ?? 0);
            break;
        case SortColumn.Date2CurrentStock:
            if (sl1.quantities.length < 3) break;
            val = (sl1.quantities[2].currentStock ?? 0) - (sl2.quantities[2].currentStock ?? 0);
            break;
        case SortColumn.Date2Available:
            if (sl1.quantities.length < 3) break;
            val = (sl1.quantities[2].availableStock ?? 0) - (sl2.quantities[2].availableStock ?? 0);
            break;
        case SortColumn.Date2AllocatedPaid:
            if (sl1.quantities.length < 3) break;
            val = (sl1.quantities[2].paidAllocatedStock ?? 0) - (sl2.quantities[2].paidAllocatedStock ?? 0);
            break;
        case SortColumn.Date2AllocatedUnpaid:
            if (sl1.quantities.length < 3) break;
            val = (sl1.quantities[2].unpaidAllocatedStock ?? 0) - (sl2.quantities[2].unpaidAllocatedStock ?? 0);
            break;
        default:
            alert(`Unknown sort column ${sort.column}`);
    }
    return val * sort.direction;
}

function getItemClass(status: ProductStockStatus) {
    switch (status) {
        case ProductStockStatus.LowStock:
            return 'low-stock';
        case ProductStockStatus.OutOfStock:
            return 'out-of-stock';
        case ProductStockStatus.Ok:
            return '';
    }
}


const mapStateToProps = (state: ApplicationState) => ({
    stockDate: state.productStock.overviewStockDate,
    stockLevels: state.productStock.overviewStockLevels,
    isLoadingProductStock: state.productStock.isLoadingOverview,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
    loadVenueStockOverview: bindActionCreators(ProductStockActions.actionCreators.loadVenueStockOverview, dispatch),
    logout: bindActionCreators(LoginActions.actionCreators.logout, dispatch),
});

export default connect(
    mapStateToProps,   
    mapDispatchToProps,
)(StockLevelOverview);
